import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import {
  HomeIcon,
  BuildingOffice2Icon,
  GlobeAsiaAustraliaIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  KeyIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  BriefcaseIcon,
  UserGroupIcon,
  BuildingLibraryIcon,
  TruckIcon,
  ScaleIcon,
  CalculatorIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  BeakerIcon,
  CubeIcon,
  HeartIcon,
  StarIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ShoppingBagIcon,
  ReceiptPercentIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BoltIcon,
  FireIcon,
  SunIcon,
  MoonIcon,
  CloudIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

// Icon mapping
const iconMap = {
  HomeIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ReceiptPercentIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  ArrowTrendingUpIcon,
  CalculatorIcon,
  ShieldCheckIcon,
  KeyIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  BriefcaseIcon,
  UserGroupIcon,
  TruckIcon,
  ScaleIcon,
  WrenchScrewdriverIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  BeakerIcon,
  CubeIcon,
  HeartIcon,
  StarIcon,
  LightBulbIcon,
  AcademicCapIcon,
  BookOpenIcon,
  GlobeAsiaAustraliaIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ShoppingBagIcon,
  SparklesIcon,
  RocketLaunchIcon,
  BoltIcon,
  FireIcon,
  SunIcon,
  MoonIcon,
  CloudIcon,
  PlusIcon
};

const ProductsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine if Tamil is selected
  const isTamil = i18n.language === 'ta';

  // Static Tamil Products
  const tamilProducts = [
    {
      id: 'plot-construction',
      title: t('products.items.plotConstruction.title'),
      description: t('products.items.plotConstruction.description'),
      features: t('products.items.plotConstruction.features', { returnObjects: true }) || [],
      icon: iconMap['HomeIcon'],
      image: 'img/Products/Construction-Loan.webp',
      gradient_from: 'primary-500',
      gradient_to: 'primary-600',
      href: '/plot-mortgage-loan'
    },
    {
      id: 'home-construction',
      title: t('products.items.homeConstruction.title'),
      description: t('products.items.homeConstruction.description'),
      features: t('products.items.homeConstruction.features', { returnObjects: true }) || [],
      icon: iconMap['BuildingOffice2Icon'],
      image: 'img/Products/Construction-Loan.webp',
      gradient_from: 'blue-500',
      gradient_to: 'blue-600',
      href: '/home-construction-loan'
    },
    {
      id: 'nri-loan',
      title: t('products.items.nriLoan.title'),
      description: t('products.items.nriLoan.description'),
      features: t('products.items.nriLoan.features', { returnObjects: true }) || [],
      icon: iconMap['GlobeAsiaAustraliaIcon'],
      image: 'img/Products/NRI.webp',
      gradient_from: 'indigo-500',
      gradient_to: 'indigo-600',
      href: '/nri-home-loan'
    },
    {
      id: 'renovation-loan',
      title: t('products.items.renovation.title'),
      description: t('products.items.renovation.description'),
      features: t('products.items.renovation.features', { returnObjects: true }) || [],
      icon: iconMap['WrenchScrewdriverIcon'],
      image: 'img/Products/Renovation-Loan.webp',
      gradient_from: 'teal-500',
      gradient_to: 'teal-600',
      href: '/home-renovation-loan'
    },
    {
      id: 'extension-loan',
      title: t('products.items.extension.title'),
      description: t('products.items.extension.description'),
      features: t('products.items.extension.features', { returnObjects: true }) || [],
      icon: iconMap['PlusIcon'],
      image: 'img/Products/Extension-Loan.webp',
      gradient_from: 'cyan-500',
      gradient_to: 'cyan-600',
      href: '/home-extension-loan'
    },
    {
      id: 'mortgage-loan',
      title: t('products.items.mortgage.title'),
      description: t('products.items.mortgage.description'),
      features: t('products.items.mortgage.features', { returnObjects: true }) || [],
      icon: iconMap['BanknotesIcon'],
      image: 'img/Products/Mortage-loan.webp',
      gradient_from: 'purple-500',
      gradient_to: 'purple-600',
      href: '/loan-against-property'
    }
  ];

  // Fetch products from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/products/active'),
          axios.get('/api/admin/products/settings')
        ]);

        const productsData = productsRes.data.data || [];

        // Transform products to include icon component and parse features
        const transformedProducts = productsData.map(product => ({
          ...product,
          icon: iconMap[product.icon_name] || HomeIcon,
          features: typeof product.features === 'string'
            ? JSON.parse(product.features)
            : product.features || [],
          image: product.image_url || 'img/Products/Construction-Loan.webp', // fallback image
          href: product.link || '#'
        }));

        setProducts(transformedProducts);
        setSettings(settingsRes.data.data || {
          section_title: t('products.title'),
          section_subtitle: t('products.subtitle')
        });
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const displayProducts = isTamil ? tamilProducts : products;

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section id="products" className="py-20 bg-white relative overflow-hidden">

      <div className="container mx-auto px-4 relative">

        {/* Title */}
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2 variants={cardVariants} className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {settings?.section_title || t('products.title')}
          </motion.h2>
          <motion.p variants={cardVariants} className="text-xl text-gray-600 max-w-3xl mx-auto">
            {settings?.section_subtitle || t('products.subtitle')}
          </motion.p>
        </motion.div>

        {/* Cards */}
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayProducts.map((product) => (
            <a href={product.href || "#"} key={product.id} className="block">
              <motion.div
                variants={cardVariants}
                className="group cursor-pointer"
                whileHover={{ y: -10 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="relative bg-white rounded-2xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden h-full">

                  <div className="relative h-48 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat transform group-hover:scale-110 transition-transform duration-700"
                      style={{ backgroundImage: `url(${product.image})` }}
                       role="img"
                      aria-label={`${product.title} - AHAM Home Loan`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-r from-${product.gradient_from} to-${product.gradient_to} opacity-80`} />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <product.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {product.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {product.description}
                    </p>

                    <div className="space-y-2 mb-6">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                          <span className="text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className="flex items-center space-x-2 text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                      <span>{t('common.learnMore')}</span>
                      <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </a>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default ProductsSection;
